import type {
  DashboardWidgetSpecArgs,
  ParseActionArgsResult,
  SofiaProposedAction,
} from "../model/types"
import {
  isRecord,
  readBoolean,
  readDraftTestCases,
  readJsonObject,
  readNumber,
  readRequestedTotal,
  readScriptFields,
  readString,
  readStringAllowEmpty,
  readStringArray,
} from "./actionArgumentReaders"
import {
  CREATE_AGENT_PHONE_CONNECTIONS,
  createAgentConnectionType,
} from "./agentActionConfig"
import { parseMetricCreateArgs } from "./metricCreateActionArgs"
import { readPersonaConfiguration } from "./personaActionConfig"
import { readCustomDimensions } from "./reportActionArgs"
import { readMetricFilter } from "./widgetActionArgs"

/**
 * Narrow one widget in Sofia's shared widget vocabulary. A chart needs a
 * metric; a text header needs its text; a table needs metric ids. Problems are
 * appended to `problems` and `undefined` is returned when the widget is unusable.
 */
function readWidgetSpec(
  raw: Record<string, unknown>,
  problems: string[],
): DashboardWidgetSpecArgs | undefined {
  const title = readString(raw, "title")
  const widget_type = readString(raw, "widget_type") ?? "chart"
  const metric_id = readString(raw, "metric_id")
  const text = readString(raw, "text")
  const metric_ids = readStringArray(raw, "metric_ids")
  const before = problems.length
  if (widget_type === "text" && !text) {
    problems.push("missing text")
  } else if (
    widget_type === "table" &&
    !(metric_ids && metric_ids.length > 0)
  ) {
    problems.push("missing metric_ids")
  } else if (widget_type === "chart" && !metric_id) {
    problems.push("missing metric_id")
  }
  if (!title) {
    problems.push("missing title")
  }
  if (!title || problems.length > before) {
    return undefined
  }
  const spec: DashboardWidgetSpecArgs = { title }
  if (widget_type !== "chart") {
    spec.widget_type = widget_type
  }
  const strings = [
    "metric_id",
    "visualization_type",
    "data_source",
    "aggregation",
    "metric_output_type",
    "group_by",
    "group_by_metadata_key",
    "bucket_interval",
    "units",
    "text",
    "width",
  ] as const
  for (const key of strings) {
    const value = readString(raw, key)
    if (value !== undefined) {
      spec[key] = value
    }
  }
  for (const key of ["stacked", "show_as_percentage"] as const) {
    const value = readBoolean(raw, key)
    if (value !== undefined) {
      spec[key] = value
    }
  }
  const precision = readNumber(raw, "precision")
  if (precision !== undefined) {
    spec.precision = precision
  }
  for (const key of ["agent_ids", "metric_ids"] as const) {
    const value = readStringArray(raw, key)
    if (value && value.length > 0) {
      spec[key] = value
    }
  }
  const metric_filter = readMetricFilter(raw)
  if (metric_filter) {
    spec.metric_filter = metric_filter
  }
  return spec
}

export function parseResourceActionArgs(
  action: SofiaProposedAction,
): ParseActionArgsResult | undefined {
  const problems: string[] = []
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- This parser owns only resource creation actions.
  switch (action.kind) {
    case "duplicate_persona": {
      const persona_id = readString(action.args, "persona_id")
      if (!persona_id) {
        return { ok: false, problems: ["missing persona_id"] }
      }
      return {
        ok: true,
        action: { kind: "duplicate_persona", args: { persona_id } },
      }
    }
    case "duplicate_agent": {
      const agent_id = readString(action.args, "agent_id")
      if (!agent_id) {
        return { ok: false, problems: ["missing agent_id"] }
      }
      return {
        ok: true,
        action: { kind: "duplicate_agent", args: { agent_id } },
      }
    }
    case "duplicate_test_set": {
      const test_set_id = readString(action.args, "test_set_id")
      if (!test_set_id) {
        return { ok: false, problems: ["missing test_set_id"] }
      }
      return {
        ok: true,
        action: { kind: "duplicate_test_set", args: { test_set_id } },
      }
    }
    case "create_agent": {
      const display_name = readString(action.args, "display_name")
      const connection_type = createAgentConnectionType(action.args)
      const phone_number = readString(action.args, "phone_number")
      const endpoint = readString(action.args, "endpoint")
      if (!display_name) {
        problems.push("missing display_name")
      }
      if (!connection_type) {
        problems.push("invalid connection_type")
      }
      const usesPhone =
        connection_type &&
        CREATE_AGENT_PHONE_CONNECTIONS.includes(connection_type)
      if (usesPhone && !phone_number) {
        problems.push("missing phone_number")
      }
      if (usesPhone && endpoint) {
        problems.push("unexpected endpoint")
      }
      if (connection_type && !usesPhone && !endpoint) {
        problems.push("missing endpoint")
      }
      if (connection_type && !usesPhone && phone_number) {
        problems.push("unexpected phone_number")
      }
      if (
        problems.length > 0 ||
        !display_name ||
        !connection_type ||
        (usesPhone ? !phone_number : !endpoint)
      ) {
        return { ok: false, problems }
      }
      return {
        ok: true,
        action: {
          kind: "create_agent",
          args: {
            display_name,
            connection_type,
            ...(usesPhone ? { phone_number } : { endpoint }),
          },
        },
      }
    }
    case "create_test_set": {
      const display_name = readString(action.args, "display_name")
      if (!display_name) {
        return { ok: false, problems: ["missing display_name"] }
      }
      const hasCases = "test_cases" in action.args
      const test_cases =
        Array.isArray(action.args.test_cases) &&
        action.args.test_cases.length === 0
          ? []
          : hasCases
            ? readDraftTestCases(action.args, problems)
            : undefined
      if (hasCases && (!test_cases || problems.length)) {
        return {
          ok: false,
          problems: problems.length ? problems : ["invalid initial test cases"],
        }
      }
      return {
        ok: true,
        action: {
          kind: "create_test_set",
          args: {
            display_name,
            ...(readString(action.args, "description")
              ? { description: readString(action.args, "description") }
              : {}),
            ...(test_cases ? { test_cases } : {}),
          },
        },
      }
    }
    case "add_test_case": {
      const test_set_id = readString(action.args, "test_set_id")
      const input_str = readString(action.args, "input_str")
      const expected_output_json = readJsonObject(
        action.args,
        "expected_output_json",
      )
      if (!test_set_id) {
        problems.push("missing test_set_id")
      }
      if (!input_str) {
        problems.push("missing input_str")
      }
      if (
        "expected_output_json" in action.args &&
        expected_output_json === undefined
      ) {
        problems.push("expected_output_json must be a JSON object")
      }
      const { input_type, script_turns } = readScriptFields(
        action.args,
        problems,
      )
      if (problems.length > 0 || !test_set_id || !input_str) {
        return { ok: false, problems }
      }
      return {
        ok: true,
        action: {
          kind: "add_test_case",
          args: {
            test_set_id,
            input_str,
            description: readString(action.args, "description"),
            expected_behaviors: readStringArray(
              action.args,
              "expected_behaviors",
            ),
            expected_output_json,
            input_type,
            script_turns,
          },
        },
      }
    }
    case "draft_test_cases": {
      const test_cases = readDraftTestCases(action.args, problems)
      if (!test_cases) {
        return {
          ok: false,
          problems: problems.length
            ? problems
            : ["test_cases must contain 1-50 valid test cases"],
        }
      }
      const requested_total = readRequestedTotal(
        action.args,
        test_cases.length,
        problems,
      )
      if (problems.length > 0) {
        return { ok: false, problems }
      }
      return {
        ok: true,
        action: {
          kind: "draft_test_cases",
          args: {
            test_set_id: readString(action.args, "test_set_id"),
            test_cases,
            ...(requested_total !== undefined ? { requested_total } : {}),
          },
        },
      }
    }
    case "create_persona": {
      const name = readString(action.args, "name")
      if (!name) {
        return { ok: false, problems: ["missing name"] }
      }
      return {
        ok: true,
        action: {
          kind: "create_persona",
          args: {
            name,
            persona_prompt: readStringAllowEmpty(action.args, "persona_prompt"),
            voice_name: readString(action.args, "voice_name"),
            language_code: readString(action.args, "language_code"),
            ...readPersonaConfiguration(action.args),
          },
        },
      }
    }
    case "create_metric":
      return parseMetricCreateArgs(action)
    case "create_dashboard": {
      const display_name = readString(action.args, "display_name")
      if (!display_name) {
        return { ok: false, problems: ["missing display_name"] }
      }
      return {
        ok: true,
        action: {
          kind: "create_dashboard",
          args: {
            display_name,
            description: readString(action.args, "description"),
          },
        },
      }
    }
    case "create_report": {
      const name = readString(action.args, "name")
      const run_ids = readStringArray(action.args, "run_ids")
      if (!name) {
        problems.push("missing name")
      }
      if (!run_ids || run_ids.length === 0) {
        problems.push("missing run_ids")
      }
      if (problems.length > 0 || !name || !run_ids) {
        return { ok: false, problems }
      }
      return {
        ok: true,
        action: {
          kind: "create_report",
          args: {
            name,
            run_ids,
            compare_by: readString(action.args, "compare_by"),
            metadata_key: readString(action.args, "metadata_key"),
            view_mode: readString(action.args, "view_mode"),
            custom_dimensions: readCustomDimensions(action.args),
          },
        },
      }
    }
    case "create_monitor": {
      const name = readString(action.args, "name")
      const metric_id = readString(action.args, "metric_id")
      const operator = readString(action.args, "operator")
      const threshold_float = readNumber(action.args, "threshold_float")
      if (!name) {
        problems.push("missing name")
      }
      if (!metric_id) {
        problems.push("missing metric_id")
      }
      if (!operator) {
        problems.push("missing operator")
      }
      if (threshold_float === undefined) {
        problems.push("missing threshold_float")
      }
      if (
        problems.length > 0 ||
        !name ||
        !metric_id ||
        !operator ||
        threshold_float === undefined
      ) {
        return { ok: false, problems }
      }
      return {
        ok: true,
        action: {
          kind: "create_monitor",
          args: {
            name,
            metric_id,
            operator,
            threshold_float,
            scope: readString(action.args, "scope"),
          },
        },
      }
    }
    case "create_widget": {
      const dashboard_id = readString(action.args, "dashboard_id")
      if (!dashboard_id) {
        problems.push("missing dashboard_id")
      }
      const spec = readWidgetSpec(action.args, problems)
      if (problems.length > 0 || !dashboard_id || !spec) {
        return { ok: false, problems }
      }
      return {
        ok: true,
        action: { kind: "create_widget", args: { dashboard_id, ...spec } },
      }
    }
    case "build_dashboard": {
      const display_name = readString(action.args, "display_name")
      const dashboard_id = readString(action.args, "dashboard_id")
      const rawWidgets = isRecord(action.args) ? action.args.widgets : undefined
      if (!display_name && !dashboard_id) {
        problems.push("missing display_name or dashboard_id")
      }
      if (!Array.isArray(rawWidgets) || rawWidgets.length === 0) {
        problems.push("missing widgets")
      }
      const widgets: DashboardWidgetSpecArgs[] = []
      if (Array.isArray(rawWidgets)) {
        rawWidgets.forEach((raw, index) => {
          const widgetProblems: string[] = []
          const spec = isRecord(raw)
            ? readWidgetSpec(raw, widgetProblems)
            : undefined
          if (!spec) {
            problems.push(
              `widgets[${index}]: ${widgetProblems.join(", ") || "not an object"}`,
            )
            return
          }
          widgets.push(spec)
        })
      }
      if (problems.length > 0) {
        return { ok: false, problems }
      }
      return {
        ok: true,
        action: {
          kind: "build_dashboard",
          args: {
            display_name,
            description: readString(action.args, "description"),
            dashboard_id,
            widgets,
          },
        },
      }
    }
    default:
      return undefined
  }
}
